import { useEffect, useMemo, useState } from "react";
import { GripVertical, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Switch,
} from "@sportscanner/ui";
import { apiDelete, apiGet, apiPut, getApiErrorCode } from "@/lib/api";
import {
  useMe,
  useSubscriptionActive,
  type MeClub,
  type MeCourt,
  type MeSport,
} from "@/lib/me";
import ConfirmDialog from "@/components/confirm-dialog";
import BookingImpactDialog from "@/components/booking-impact-dialog";
import { formatCourtLabel, formatRSD } from "@/components/booking/helpers";
import CourtFormDialog from "./court-form-dialog";

interface Props {
  clubId: string;
}

interface DeleteState {
  court: MeCourt;
  open: boolean;
}

interface ToggleState {
  court: MeCourt;
  nextActive: boolean;
  // Set on hide (nextActive=false) after the preflight returns a non-zero
  // count. The dialog then shows the BookingImpactDialog warn variant with
  // the count up-front instead of the generic confirm description.
  impactCount?: number;
}

// A group of courts that can be reordered together. Drag is restricted to a
// single group — moving an outdoor court into the indoor list (or across
// sports) is intentionally not supported.
interface Group {
  key: string; // `${sportId}::${indoor ? "indoor" : "outdoor"}`
  sportId: string;
  isIndoor: boolean;
  courts: MeCourt[];
}

interface DragHover {
  targetId: string;
  position: "above" | "below";
}

function sortServerCourts(courts: MeCourt[], sports: MeSport[]): MeCourt[] {
  return [...courts].sort((a, b) => {
    const sa = sports.find((s) => s.id === a.sportId)?.name ?? "";
    const sb = sports.find((s) => s.id === b.sportId)?.name ?? "";
    if (sa !== sb) return sa.localeCompare(sb);
    if (a.isIndoor !== b.isIndoor) return a.isIndoor ? -1 : 1;
    const oa = a.displayOrder ?? Number.MAX_SAFE_INTEGER;
    const ob = b.displayOrder ?? Number.MAX_SAFE_INTEGER;
    if (oa !== ob) return oa - ob;
    const an = Number(a.name);
    const bn = Number(b.name);
    if (!isNaN(an) && !isNaN(bn)) return an - bn;
    return a.name.localeCompare(b.name);
  });
}

function buildGroups(courts: MeCourt[]): Group[] {
  const groups: Group[] = [];
  const index = new Map<string, Group>();
  for (const court of courts) {
    const key = `${court.sportId}::${court.isIndoor ? "indoor" : "outdoor"}`;
    let group = index.get(key);
    if (!group) {
      group = {
        key,
        sportId: court.sportId,
        isIndoor: court.isIndoor,
        courts: [],
      };
      index.set(key, group);
      groups.push(group);
    }
    group.courts.push(court);
  }
  return groups;
}

export default function CourtsList({ clubId }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { refresh: refreshMe } = useMe();
  const subscriptionActive = useSubscriptionActive();

  const clubQuery = useQuery({
    queryKey: ["admin", "clubs", clubId],
    queryFn: async () => {
      const data = await apiGet<{ club: MeClub }>(`/api/admin/clubs/${clubId}`);
      return data.club;
    },
  });

  const sports: MeSport[] = clubQuery.data?.sports ?? [];
  const courts: MeCourt[] = clubQuery.data?.courts ?? [];
  // Free clubs don't charge — hide court price display + price input.
  const isFree = clubQuery.data?.isFree ?? false;

  // Half-courts (children of a split court) aren't top-level rows — they're
  // rendered nested under their parent and managed via the parent's editor.
  const wholeCourts = useMemo(
    () => courts.filter((c) => c.parentCourtId === null),
    [courts],
  );
  const halvesByParent = useMemo(() => {
    const m = new Map<string, MeCourt[]>();
    for (const c of courts) {
      if (!c.parentCourtId) continue;
      const list = m.get(c.parentCourtId);
      if (list) list.push(c);
      else m.set(c.parentCourtId, [c]);
    }
    // Stable left/right order by name.
    for (const list of m.values())
      list.sort((a, b) => a.name.localeCompare(b.name));
    return m;
  }, [courts]);

  const sortedServerCourts = useMemo(
    () => sortServerCourts(wholeCourts, sports),
    [wholeCourts, sports],
  );

  // `orderedIds` is null until the user drags. After drag, it owns the order;
  // after a successful save it resets to null so the server order takes over
  // again. Courts that appear in the server data but not in `orderedIds` (e.g.
  // a new court added mid-edit) are appended in their server-sorted position.
  const [orderedIds, setOrderedIds] = useState<string[] | null>(null);

  const effectiveCourts = useMemo(() => {
    if (!orderedIds) return sortedServerCourts;
    const byId = new Map(sortedServerCourts.map((c) => [c.id, c]));
    const seen = new Set<string>();
    const result: MeCourt[] = [];
    for (const id of orderedIds) {
      const c = byId.get(id);
      if (c) {
        result.push(c);
        seen.add(id);
      }
    }
    for (const c of sortedServerCourts) {
      if (!seen.has(c.id)) result.push(c);
    }
    return result;
  }, [orderedIds, sortedServerCourts]);

  const isDirty = useMemo(() => {
    if (orderedIds === null) return false;
    if (effectiveCourts.length !== sortedServerCourts.length) return false;
    for (let i = 0; i < effectiveCourts.length; i++) {
      if (effectiveCourts[i].id !== sortedServerCourts[i].id) return true;
    }
    return false;
  }, [orderedIds, effectiveCourts, sortedServerCourts]);

  const groups = useMemo(() => buildGroups(effectiveCourts), [effectiveCourts]);
  const sportsWithCourts = useMemo(() => {
    const ids = new Set(effectiveCourts.map((c) => c.sportId));
    return sports.filter((s) => ids.has(s.id));
  }, [effectiveCourts, sports]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<MeCourt | null>(null);
  const [deleteState, setDeleteState] = useState<DeleteState | null>(null);
  const [toggleState, setToggleState] = useState<ToggleState | null>(null);
  const [togglePreflighting, setTogglePreflighting] = useState<string | null>(
    null,
  );

  async function requestToggleActive(court: MeCourt, nextActive: boolean) {
    if (nextActive) {
      // Showing a hidden court is non-destructive — no preflight needed.
      setToggleState({ court, nextActive });
      return;
    }
    setTogglePreflighting(court.id);
    try {
      const params = new URLSearchParams({
        kind: "court-hide",
        courtId: court.id,
      });
      const res = await apiGet<{ severity: string; count: number }>(
        `/api/admin/clubs/${clubId}/edit-impact?${params.toString()}`,
      );
      setToggleState({
        court,
        nextActive,
        // 0 falls back to the generic hide description in the dialog body.
        impactCount: res.count > 0 ? res.count : undefined,
      });
    } catch {
      // Preflight failure shouldn't strand the user — drop into the generic
      // confirm dialog without the count. Server still backstops everything.
      setToggleState({ court, nextActive });
    } finally {
      setTogglePreflighting(null);
    }
  }

  // ── drag state ──
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hover, setHover] = useState<DragHover | null>(null);
  // Clear hover/drag state if the user drops outside any row.
  useEffect(() => {
    if (!draggingId) return;
    const onDragEnd = () => {
      setDraggingId(null);
      setHover(null);
    };
    window.addEventListener("dragend", onDragEnd);
    return () => window.removeEventListener("dragend", onDragEnd);
  }, [draggingId]);

  async function invalidate() {
    await queryClient.invalidateQueries({
      queryKey: ["admin", "clubs", clubId],
    });
    await refreshMe();
  }

  const toggleActiveMutation = useMutation({
    mutationFn: async (vars: { court: MeCourt; isActive: boolean }) => {
      return apiPut(`/api/admin/clubs/${clubId}/courts/${vars.court.id}`, {
        sportId: vars.court.sportId,
        name: vars.court.name,
        isIndoor: vars.court.isIndoor,
        // Round-trip surface so the toggle doesn't unset it server-side.
        surface: vars.court.surface,
        pricePerSession: Number(vars.court.pricePerSession),
        workingHoursStart: vars.court.workingHoursStart,
        workingHoursEnd: vars.court.workingHoursEnd,
        isActive: vars.isActive,
      });
    },
    onSuccess: async () => {
      setToggleState(null);
      await invalidate();
    },
    onError: (err) => {
      setToggleState(null);
      toast.error(t(`errors.api.${getApiErrorCode(err)}`));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (courtId: string) => {
      return apiDelete<{ ok: true; softDeleted: boolean }>(
        `/api/admin/clubs/${clubId}/courts/${courtId}`,
      );
    },
    onSuccess: async (data) => {
      if (data.softDeleted) {
        toast.success(t("courts.delete.softDeletedToast"));
      } else {
        toast.success(t("courts.delete.deletedToast"));
      }
      setDeleteState(null);
      await invalidate();
    },
    onError: (err: unknown) => {
      const code = getApiErrorCode(err);
      const count =
        err && typeof err === "object" && "count" in err
          ? (err as { count?: number }).count
          : undefined;
      if (code === "COURT_HAS_FUTURE_BOOKINGS" && typeof count === "number") {
        toast.error(t("courts.delete.cannotDeleteFutureBookings", { count }));
      } else {
        toast.error(t(`errors.api.${code}`));
      }
      setDeleteState(null);
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (courtIds: string[]) => {
      return apiPut(`/api/admin/clubs/${clubId}/courts/order`, { courtIds });
    },
    onSuccess: async () => {
      toast.success(t("courts.orderSavedToast"));
      setOrderedIds(null);
      await invalidate();
    },
    onError: (err) => {
      toast.error(t(`errors.api.${getApiErrorCode(err)}`));
    },
  });

  function handleDragStart(courtId: string) {
    setDraggingId(courtId);
  }

  function handleDragOver(e: React.DragEvent, target: MeCourt) {
    if (!draggingId || draggingId === target.id) return;
    const dragged = effectiveCourts.find((c) => c.id === draggingId);
    if (!dragged) return;
    // Drag is restricted to the same sport + indoor/outdoor group.
    if (
      dragged.sportId !== target.sportId ||
      dragged.isIndoor !== target.isIndoor
    ) {
      return;
    }
    e.preventDefault();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const position: "above" | "below" = e.clientY < midY ? "above" : "below";
    if (hover?.targetId !== target.id || hover.position !== position) {
      setHover({ targetId: target.id, position });
    }
  }

  function handleDrop(e: React.DragEvent, target: MeCourt) {
    e.preventDefault();
    const draggedId = draggingId;
    setDraggingId(null);
    const hoverState = hover;
    setHover(null);
    if (!draggedId || draggedId === target.id) return;
    const dragged = effectiveCourts.find((c) => c.id === draggedId);
    if (!dragged) return;
    if (
      dragged.sportId !== target.sportId ||
      dragged.isIndoor !== target.isIndoor
    ) {
      return;
    }

    const next = effectiveCourts.filter((c) => c.id !== draggedId);
    const targetIndex = next.findIndex((c) => c.id === target.id);
    if (targetIndex === -1) return;
    const insertAt =
      hoverState?.position === "below" ? targetIndex + 1 : targetIndex;
    next.splice(insertAt, 0, dragged);
    setOrderedIds(next.map((c) => c.id));
  }

  if (clubQuery.isLoading) {
    return (
      <div className="bg-white rounded-xl border border-neutral-200 p-6 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
      </div>
    );
  }

  // Whether to show sport headers — only meaningful when more than one sport
  // actually has courts.
  const showSportHeaders = sportsWithCourts.length > 1;
  const sportGroupsBySportId = new Map<string, Group[]>();
  for (const g of groups) {
    const existing = sportGroupsBySportId.get(g.sportId) ?? [];
    existing.push(g);
    sportGroupsBySportId.set(g.sportId, existing);
  }

  const totalCourts = effectiveCourts.length;
  const canReorder = totalCourts > 1;
  const maxCourts = clubQuery.data?.maxCourts ?? null;
  const atCap = maxCourts !== null && wholeCourts.length >= maxCourts;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="text-sm text-neutral-500">
          {wholeCourts.length === 0 ? (
            t("courts.empty")
          ) : (
            <>
              <span>
                {maxCourts !== null
                  ? t("courts.countWithCap", {
                      count: wholeCourts.length,
                      max: maxCourts,
                    })
                  : t("courts.count", { count: wholeCourts.length })}
              </span>
              {canReorder && (
                <span className="block text-xs text-neutral-400 mt-1">
                  {t("courts.dragToSort")}
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canReorder && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (orderedIds) reorderMutation.mutate(orderedIds);
              }}
              disabled={
                !isDirty || reorderMutation.isPending || !subscriptionActive
              }
              className="rounded-full"
            >
              {reorderMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t("courts.saveOrder")
              )}
            </Button>
          )}
          <Button
            type="button"
            onClick={() => setCreateOpen(true)}
            disabled={!subscriptionActive || atCap}
            title={atCap ? t("courts.maxReachedHint") : undefined}
            className="rounded-full bg-primary-600 hover:bg-primary-700 text-white"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            {t("courts.addCourt")}
          </Button>
        </div>
      </div>

      {wholeCourts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-neutral-200 px-6 py-10 text-center">
          <p className="text-sm text-neutral-500">{t("courts.empty")}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {sportsWithCourts.map((sport) => {
            const sportGroups = sportGroupsBySportId.get(sport.id) ?? [];
            // Only show indoor/outdoor sub-headers when both kinds exist for
            // this sport — single-type sports stay flat.
            const showIndoorOutdoorHeaders = sportGroups.length > 1;
            return (
              <div key={sport.id} className="space-y-3">
                {showSportHeaders && (
                  <h2 className="text-sm font-semibold text-neutral-700">
                    {t(`sports.${sport.name}`)}
                  </h2>
                )}
                <div
                  className={
                    showIndoorOutdoorHeaders
                      ? "space-y-4"
                      : showSportHeaders
                        ? ""
                        : ""
                  }
                >
                  {sportGroups.map((group, gIdx) => (
                    <div key={group.key} className="space-y-2">
                      {showIndoorOutdoorHeaders && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium uppercase tracking-wide text-neutral-400">
                            {group.isIndoor
                              ? t("courts.row.indoor")
                              : t("courts.row.outdoor")}
                          </span>
                          <div className="flex-1 h-px bg-neutral-100" />
                        </div>
                      )}
                      {!showIndoorOutdoorHeaders &&
                        showSportHeaders &&
                        gIdx > 0 && <div className="h-px bg-neutral-100" />}
                      <div className="space-y-2">
                        {group.courts.map((court) => (
                          <CourtRow
                            key={court.id}
                            court={court}
                            sport={sport}
                            isFree={isFree}
                            halves={halvesByParent.get(court.id)}
                            isDragging={draggingId === court.id}
                            hover={hover?.targetId === court.id ? hover : null}
                            canReorder={canReorder && group.courts.length > 1}
                            onDragStart={() => handleDragStart(court.id)}
                            onDragOver={(e) => handleDragOver(e, court)}
                            onDrop={(e) => handleDrop(e, court)}
                            onDragLeave={() => {
                              if (hover?.targetId === court.id) setHover(null);
                            }}
                            onToggleActive={(nextActive) =>
                              requestToggleActive(court, nextActive)
                            }
                            onEdit={() => setEditing(court)}
                            onDelete={() =>
                              setDeleteState({ court, open: true })
                            }
                            toggleDisabled={
                              toggleActiveMutation.isPending ||
                              togglePreflighting === court.id ||
                              !subscriptionActive
                            }
                            writeDisabled={!subscriptionActive}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CourtFormDialog
        mode="create"
        clubId={clubId}
        sports={sports}
        isFree={isFree}
        defaultWorkingHoursStart={clubQuery.data?.workingHoursStart ?? "08:00"}
        defaultWorkingHoursEnd={clubQuery.data?.workingHoursEnd ?? "22:00"}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSaved={invalidate}
      />

      <CourtFormDialog
        mode="edit"
        clubId={clubId}
        sports={sports}
        isFree={isFree}
        defaultWorkingHoursStart={clubQuery.data?.workingHoursStart ?? "08:00"}
        defaultWorkingHoursEnd={clubQuery.data?.workingHoursEnd ?? "22:00"}
        court={editing ?? undefined}
        halves={editing ? halvesByParent.get(editing.id) : undefined}
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        onSaved={invalidate}
      />

      {/* Hide with upcoming bookings → warn dialog with the count up-front.
          Everything else (show, or hide of a court with no bookings) uses the
          generic confirm. */}
      {toggleState && !toggleState.nextActive && toggleState.impactCount ? (
        <BookingImpactDialog
          open
          onOpenChange={(o) => {
            if (!o) setToggleState(null);
          }}
          severity="warn"
          count={toggleState.impactCount}
          title={t("bookingImpact.courtHide.warnTitle")}
          description={t("bookingImpact.courtHide.warnDescription")}
          confirmLabel={t("bookingImpact.actions.hideAnyway")}
          isPending={toggleActiveMutation.isPending}
          onConfirm={() =>
            toggleActiveMutation.mutate({
              court: toggleState.court,
              isActive: false,
            })
          }
        />
      ) : (
        <ConfirmDialog
          open={!!toggleState}
          onOpenChange={(o) => {
            if (!o) setToggleState(null);
          }}
          title={
            toggleState?.nextActive
              ? t("courts.toggleConfirm.showTitle")
              : t("courts.toggleConfirm.hideTitle")
          }
          description={
            toggleState
              ? toggleState.nextActive
                ? t("courts.toggleConfirm.showDescription", {
                    name: toggleState.court.name,
                  })
                : t("courts.toggleConfirm.hideDescription", {
                    name: toggleState.court.name,
                  })
              : undefined
          }
          confirmLabel={t("common.confirm")}
          isPending={toggleActiveMutation.isPending}
          onConfirm={() => {
            if (toggleState) {
              toggleActiveMutation.mutate({
                court: toggleState.court,
                isActive: toggleState.nextActive,
              });
            }
          }}
        />
      )}

      <AlertDialog
        open={!!deleteState?.open}
        onOpenChange={(o) => setDeleteState((s) => (s ? { ...s, open: o } : s))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("courts.delete.confirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("courts.delete.confirmDescription", {
                name: deleteState?.court.name ?? "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (deleteState) deleteMutation.mutate(deleteState.court.id);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t("common.delete")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface CourtRowProps {
  court: MeCourt;
  sport: MeSport;
  // Hide the price segment when the club doesn't charge.
  isFree: boolean;
  // The court's two half-courts (basketball split), rendered as nested rows.
  halves?: MeCourt[];
  isDragging: boolean;
  hover: DragHover | null;
  canReorder: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onToggleActive: (nextActive: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
  toggleDisabled: boolean;
  // Edit/delete buttons are disabled when the club's subscription has lapsed.
  writeDisabled: boolean;
}

function CourtRow({
  court,
  sport,
  isFree,
  halves,
  isDragging,
  hover,
  canReorder,
  onDragStart,
  onDragOver,
  onDrop,
  onDragLeave,
  onToggleActive,
  onEdit,
  onDelete,
  toggleDisabled,
  writeDisabled,
}: CourtRowProps) {
  const { t } = useTranslation();
  const sportLabel = t(`sports.${sport.name}`);
  return (
    <div className="relative">
      {hover?.position === "above" && (
        <div className="absolute inset-x-0 -top-1 h-0.5 bg-primary-500 rounded-full pointer-events-none" />
      )}
      {hover?.position === "below" && (
        <div className="absolute inset-x-0 -bottom-1 h-0.5 bg-primary-500 rounded-full pointer-events-none" />
      )}
      <div
        draggable={canReorder}
        onDragStart={canReorder ? onDragStart : undefined}
        onDragOver={canReorder ? onDragOver : undefined}
        onDrop={canReorder ? onDrop : undefined}
        onDragLeave={canReorder ? onDragLeave : undefined}
        className={`flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 px-4 py-3 rounded-lg border bg-white ${
          court.isActive
            ? "border-neutral-200"
            : "border-neutral-200 bg-neutral-50/40 opacity-70"
        } ${isDragging ? "opacity-40" : ""}`}
      >
        {canReorder && (
          <span
            className="text-neutral-300 hover:text-neutral-500 cursor-grab active:cursor-grabbing select-none"
            aria-hidden="true"
          >
            <GripVertical className="w-4 h-4" />
          </span>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-neutral-800">
            {formatCourtLabel(court.name, t("bookings.form.court"))}{" "}
            <span className="text-neutral-400 font-normal">· {sportLabel}</span>
          </p>
          <p className="text-xs text-neutral-500 mt-0.5">
            {court.isIndoor
              ? t("onboarding.courts.indoor")
              : t("onboarding.courts.outdoor")}
            {court.surface ? <> · {t(`surfaces.${court.surface}`)}</> : null}
            {!isFree && (
              <>
                {" · "}
                {formatRSD(court.pricePerSession, t("bookings.details.free"))}
              </>
            )}
            {court.workingHoursStart && court.workingHoursEnd ? (
              <>
                {" "}
                · {court.workingHoursStart}–{court.workingHoursEnd}
              </>
            ) : null}
            {!court.isActive && (
              <>
                {" "}
                ·{" "}
                <span className="text-amber-600 font-medium">
                  {t("courts.row.inactive")}
                </span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center justify-between sm:justify-start gap-3 sm:border-0 border-t border-neutral-100 pt-3 sm:pt-0">
          <div className="flex items-center gap-2">
            <span className="text-xs text-neutral-500">
              {t("courts.row.activeToggle")}
            </span>
            <Switch
              checked={court.isActive}
              onCheckedChange={onToggleActive}
              disabled={toggleDisabled}
            />
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onEdit}
              disabled={writeDisabled}
              className="text-neutral-400 hover:text-primary-600 transition-colors p-2 -m-1 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-neutral-400"
              aria-label={t("courts.editCourt")}
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={writeDisabled}
              className="text-neutral-400 hover:text-red-500 transition-colors p-2 -m-1 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-neutral-400"
              aria-label={t("common.delete")}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      {halves && halves.length > 0 && (
        <div className="ml-6 mt-1.5 space-y-1.5">
          {halves.map((h) => (
            <div
              key={h.id}
              className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-md border border-dashed border-neutral-200 bg-neutral-50/60 px-3 py-2 text-xs text-neutral-600"
            >
              <span className="font-medium text-neutral-700">{h.name}</span>
              <span className="text-neutral-400">· {t("courts.row.half")}</span>
              {!isFree && (
                <span className="text-neutral-500">
                  · {formatRSD(h.pricePerSession, t("bookings.details.free"))}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
